/**
 * Client-side owner-invoice PDF generation (jsPDF), shared by the PM Owner
 * Invoices page and the owner portal's one-click download. One implementation
 * so the header (Invoice Details config + logo), per-property grouping, and
 * footer blocks can't drift between the two surfaces.
 */

import type { OwnerInvoiceLineItem, OwnerInvoiceConfig } from "@shared/schema";
import { groupOwnerInvoiceLineItems } from "@shared/owner-invoice-render";

export interface OwnerInvoicePdfInvoice {
  invoiceNumber: string;
  billingMonth: string;
  dueDate: string | null;
  notes: string | null;
  totalDueCents: number;
  lineItems: OwnerInvoiceLineItem[];
}

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "$0.00";
  return `$${(cents / 100).toFixed(2)}`;
}

function billingMonthLabel(bm: string): string {
  const [y, m] = bm.split("-");
  return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
}

/**
 * Fetch the logo (credentialed — both the PM image route and the owner-portal
 * proxy are session-auth'd) and measure its natural size so it can be drawn at
 * its true aspect ratio. Returns null on any failure or non-PNG/JPEG type —
 * a missing logo must never break the PDF.
 */
async function loadLogo(src: string): Promise<{ dataUrl: string; format: "PNG" | "JPEG"; width: number; height: number } | null> {
  try {
    const resp = await fetch(src, { credentials: "include" });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    if (blob.type !== "image/png" && blob.type !== "image/jpeg") return null;
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error("Logo read failed"));
      reader.readAsDataURL(blob);
    });
    const dims = await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error("Logo decode failed"));
      img.src = dataUrl;
    });
    if (!dims.width || !dims.height) return null;
    return { dataUrl, format: blob.type === "image/png" ? "PNG" : "JPEG", ...dims };
  } catch {
    return null;
  }
}

export async function downloadOwnerInvoicePdf(opts: {
  invoice: OwnerInvoicePdfInvoice;
  ownerName: string;
  statusLabel: string;
  invoiceConfig: OwnerInvoiceConfig | null;
  /** URL to fetch the logo from (PM: the config logoUrl; owner portal: the pm-logo proxy). */
  logoSrc?: string | null;
}): Promise<void> {
  const { invoice, ownerName, statusLabel, invoiceConfig } = opts;
  const { default: jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");
  const doc = new jsPDF();
  const monthLabel = billingMonthLabel(invoice.billingMonth);

  let headerY = 20;

  if (opts.logoSrc) {
    const logo = await loadLogo(opts.logoSrc);
    if (logo) {
      // Fit inside a 40×16mm box at the logo's natural aspect ratio (never stretch).
      const maxW = 40;
      const maxH = 16;
      const scale = Math.min(maxW / logo.width, maxH / logo.height);
      const drawW = logo.width * scale;
      const drawH = logo.height * scale;
      doc.addImage(logo.dataUrl, logo.format, 14, 12, drawW, drawH, undefined, "FAST");
      headerY = 12 + drawH + 8;
    }
  }

  doc.setFontSize(18);
  doc.setTextColor(0, 82, 204);
  doc.text(
    invoiceConfig?.companyName
      ? `${invoiceConfig.companyName} — Invoice`
      : "PadPulse — Property Management Invoice",
    14,
    headerY,
  );

  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  let y = headerY + 8;
  if (invoiceConfig) {
    const cityLine = `${[invoiceConfig.city, invoiceConfig.state].filter(Boolean).join(", ")} ${invoiceConfig.postalCode ?? ""}`.trim();
    const addressLines = [
      invoiceConfig.addressLine1,
      invoiceConfig.addressLine2,
      cityLine,
      [invoiceConfig.email, invoiceConfig.phone].filter(Boolean).join(" · "),
    ].filter((l): l is string => !!l && l.trim() !== "");
    for (const line of addressLines) {
      doc.text(line, 14, y);
      y += 5;
    }
    y += 3;
  }
  doc.text(`Invoice #: ${invoice.invoiceNumber}`, 14, y);
  doc.text(`Billing Month: ${monthLabel}`, 14, y + 6);
  doc.text(`Owner: ${ownerName}`, 14, y + 12);
  doc.text(`Status: ${statusLabel}`, 14, y + 18);
  if (invoice.dueDate) doc.text(`Due Date: ${invoice.dueDate}`, 14, y + 24);
  const tableStartY = y + (invoice.dueDate ? 32 : 26);

  // One section row per property (management-fee + vendor-upcharge sublines),
  // flat list for legacy invoices without property attribution.
  const { grouped, groups } = groupOwnerInvoiceLineItems(invoice.lineItems ?? []);
  const body: Parameters<typeof autoTable>[1]["body"] = [];
  for (const group of groups) {
    if (grouped) {
      body.push([
        {
          content: group.propertyName ?? "Other charges",
          colSpan: 2,
          styles: { fontStyle: "bold", fillColor: [240, 244, 255], textColor: [50, 50, 50] },
        },
      ]);
    }
    for (const li of group.items) {
      body.push([grouped ? `    ${li.description}` : li.description, formatCents(li.amountCents)]);
    }
  }
  body.push([
    { content: "Total Due", styles: { fontStyle: "bold" } },
    { content: formatCents(invoice.totalDueCents), styles: { fontStyle: "bold" } },
  ]);
  autoTable(doc, {
    startY: tableStartY,
    head: [["Description", "Amount"]],
    body,
    headStyles: { fillColor: [0, 82, 204] },
    columnStyles: { 1: { halign: "right" } },
  });

  let footY = ((doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable?.finalY ?? tableStartY) + 10;
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  const footerBlocks: Array<[string, string]> = [];
  if (invoice.notes) footerBlocks.push(["Notes:", invoice.notes]);
  if (invoiceConfig?.paymentInstructions) footerBlocks.push(["Payment Instructions:", invoiceConfig.paymentInstructions]);
  if (invoiceConfig?.footerNotes) footerBlocks.push(["", invoiceConfig.footerNotes]);
  for (const [label, text] of footerBlocks) {
    if (label) {
      doc.text(label, 14, footY);
      footY += 6;
    }
    const wrapped = doc.splitTextToSize(text, 180);
    doc.text(wrapped, 14, footY);
    footY += wrapped.length * 5 + 4;
  }

  doc.save(`${invoice.invoiceNumber}.pdf`);
}
